import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/layout/Layout';
import Login from './pages/auth/Login';
import Dashboard from './pages/dashboard/Dashboard';
import Hospitals from './pages/hospitals/Hospitals';
import Patients from './pages/patients/Patients';
import PatientProfile from './pages/patients/PatientProfile';
import Appointments from './pages/appointments/Appointments';
import Inventory from './pages/inventory/Inventory';
import Billing from './pages/billing/Billing';
import Staff from './pages/staff/Staff';
import Doctors from './pages/doctors/Doctors';
import DoctorProfile from './pages/doctors/DoctorProfile';

const theme = createTheme({
  palette: {
    primary: { main: '#0d9488', light: '#14b8a6', dark: '#0f766e' },
    secondary: { main: '#6366f1' },
    background: { default: '#f0fdfa', paper: '#ffffff' },
    text: { primary: '#0f172a', secondary: '#64748b' },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", -apple-system, sans-serif',
    h4: { fontWeight: 800 }, h5: { fontWeight: 800 }, h6: { fontWeight: 700 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600, borderRadius: 10 } } },
    MuiCard: { styleOverrides: { root: { backgroundImage: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)', borderRadius: 16 } } },
    MuiTableCell: { styleOverrides: { root: { borderBottom: '1px solid #f1f5f9' } } },
    MuiChip: { styleOverrides: { root: { fontWeight: 600 } } },
    MuiTextField: { defaultProps: { size: 'small' } },
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
  }
});

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const AppRoutes = () => (
  <BrowserRouter>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route index element={<Navigate to="/dashboard" />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="hospitals" element={<Hospitals />} />
        <Route path="patients" element={<Patients />} />
        <Route path="patients/:id" element={<PatientProfile />} />
        <Route path="appointments" element={<Appointments />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="billing" element={<Billing />} />
        <Route path="staff" element={<Staff />} />
        <Route path="doctors" element={<Doctors />} />
        <Route path="doctors/:id" element={<DoctorProfile />} />
      </Route>
    </Routes>
  </BrowserRouter>
);

const App = () => (
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <Toaster position="top-right" toastOptions={{ style: { borderRadius: 12, fontFamily: 'Inter, sans-serif', fontSize: 14, boxShadow: '0 4px 20px rgba(0,0,0,0.12)' } }} />
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  </ThemeProvider>
);

export default App;
